import React, { useEffect } from 'react';
import { X, Keyboard, Mouse } from 'lucide-react';
import { SHORTCUTS } from '@/utils/shortcuts';
import { useTranslation } from '@/i18n';
import { clsx } from 'clsx';

interface ShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const scopeLabels: Record<string, { en: string; ru: string }> = {
  global: { en: 'Global', ru: 'Глобальные' },
  chat: { en: 'Chat Input', ru: 'Ввод сообщения' },
  tabs: { en: 'Tabs', ru: 'Вкладки' },
  modal: { en: 'Modals', ru: 'Модальные окна' },
};

export const ShortcutsModal: React.FC<ShortcutsModalProps> = ({ isOpen, onClose }) => {
  const { language } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const grouped = SHORTCUTS.reduce<Record<string, typeof SHORTCUTS>>((acc, s) => {
    (acc[s.scope] ??= []).push(s);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md mx-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {language === 'ru' ? 'Горячие клавиши' : 'Keyboard Shortcuts'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Close (Esc)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-5 max-h-[60vh] overflow-y-auto">
          {Object.entries(grouped).map(([scope, items]) => (
            <div key={scope}>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {language === 'ru' ? scopeLabels[scope]?.ru : scopeLabels[scope]?.en}
              </h3>
              <div className="space-y-1.5">
                {items.map(s => (
                  <div key={s.keys} className="flex items-center justify-between py-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                      {s.type === 'mouse' && <Mouse className="h-3.5 w-3.5 text-gray-400" />}
                      {(!s.type || s.type === 'keyboard') && <Keyboard className="h-3.5 w-3.5 text-gray-400" />}
                      {language === 'ru' ? s.actionRu : s.action}
                    </span>
                    <kbd className={clsx(
                      'px-2 py-0.5 text-xs font-mono rounded',
                      'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                    )}>
                      {s.keys}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
          {language === 'ru' ? 'Нажмите Esc для закрытия' : 'Press Esc to close'}
        </div>
      </div>
    </div>
  );
};
